import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useSetBetaTester } from '@/services/api/endpoints/UserService';
import type { User } from '@/types';

interface BetaTesterSectionProps {
  user: User;
}

/**
 * L-01: the per-user "Beta tester" switch. A beta tester sees card top-up and e-money (VendPay novčanik) in the app
 * while they are switched off globally. Flipping it asks for an inline confirmation first; the API writes an audit
 * row (`user.beta_tester`) for every real change, so this saves on its own — independent of the form's Save button.
 */
export function BetaTesterSection({ user }: BetaTesterSectionProps) {
  const { t } = useTranslation();
  const setBetaTester = useSetBetaTester();

  const [current, setCurrent] = useState<boolean>(!!user.isBetaTester);
  const [pending, setPending] = useState<boolean | null>(null);

  useEffect(() => {
    setCurrent(!!user.isBetaTester);
    setPending(null);
  }, [user.id, user.isBetaTester]);

  const handleConfirm = async () => {
    if (pending === null) return;
    try {
      const result = await setBetaTester.mutateAsync({ id: user.id, isBetaTester: pending });
      setCurrent(result.isBetaTester);
      toast.success(result.isBetaTester ? t('users.beta.enabled') : t('users.beta.disabled'));
    } catch {
      toast.error(t('users.beta.error'));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-medium flex items-center gap-1.5">
            <FlaskConical className="h-4 w-4 shrink-0" />
            {t('users.beta.label')}
          </p>
          <p className="text-sm text-muted-foreground">{t('users.beta.description')}</p>
        </div>
        <Switch
          checked={pending ?? current}
          disabled={setBetaTester.isPending || pending !== null}
          onCheckedChange={(checked) => setPending(checked)}
          aria-label={t('users.beta.label')}
        />
      </div>

      {pending !== null && (
        <div className="rounded-md bg-muted/50 p-3 space-y-3">
          <p className="text-sm">
            {pending
              ? t('users.beta.confirmEnable', { email: user.email })
              : t('users.beta.confirmDisable', { email: user.email })}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPending(null)} disabled={setBetaTester.isPending}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={handleConfirm} isLoading={setBetaTester.isPending}>
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
